import { Logger } from '@frontend/logger';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageTriggerName, PackageType } from '@frontend/package/types';
import { SettingKey } from '@frontend/setting/types';
import { SlotWorkflowClient } from '@frontend/slot/api';
import { useReactiveSpot } from '@frontend/spot/events';
import { Spot } from '@frontend/spot/types';
import { TransactionClient } from '@frontend/transaction/api';
import { ScreenTrigger } from '@frontend/wizard/types';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    isLoading: boolean;
    currentSpot: Spot | null;
    destinationSpot: Spot | null;
    optionAEnabled: boolean;
    optionBEnabled: boolean;
    noOptionsAvailable: boolean;
    selectedOption: 'A' | 'B' | null;
    setSelectedOption: Dispatch<SetStateAction<'A' | 'B' | null>>;
    onSubmit: () => void;
}

const useConfirmCurrentDeviceLocation = (): ViewProps => {
    const { navigationState, store, onSubmit, updateWizardData, screen_type, wizardData } = useScreen();
    const { package_type } = wizardData;
    const [isLoading, setIsLoading] = useState(true);
    const checking = useRef(false);

    const { transaction, localUpdateTransaction, refresh } = useWizardTransaction({
        wizardId: store!.wizard.id,
        store
    });
    const {
        package: _package,
        localUpdatePackage,
        refresh: refreshPackage
    } = useWizardPackage({
        wizardId: store!.wizard.id,
        store
    });

    const currentSpotId = navigationState.spot?.id;
    const { spot: currentSpot } = useReactiveSpot({ spotId: currentSpotId ?? undefined });
    const { spot: destinationSpot } = useReactiveSpot({ spotId: _package?.spot_id ?? undefined });
    const [selectedOption, setSelectedOption] = useState<'A' | 'B' | null>(null);
    const [availableSpotIds, setAvailableSpotIds] = useState<string[]>([]);

    const dropWorkflowId = store?.functionalitySettings.find((s) => s.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID)?.value;
    const pickWorkflowId = store?.functionalitySettings.find((s) => s.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID)?.value;

    useEffect(() => {
        const resolve = async () => {
            if (checking.current || !store || selectedOption) return;
            checking.current = true;
            setIsLoading(true);

            try {
                let activeTransaction = transaction;
                if (_package && (!transaction || !transaction.id)) {
                    const txn = await TransactionClient.fetchTransaction(_package.account_id!, _package.transaction_id!);
                    await localUpdateTransaction(txn);
                    activeTransaction = txn;
                }

                if (!activeTransaction || !activeTransaction.id) {
                    Logger.error('No transaction available in confirm-current-device-location');
                    return;
                }

                const pkgResult = await PackageClient.fetchPackages({
                    transaction_id: activeTransaction.id,
                    type: package_type as PackageType | undefined
                });

                let currentLocationPackages = pkgResult.results
                    .filter(
                        (p) =>
                            p.spot_id === currentSpotId &&
                            !p.state.includes('done') &&
                            !p.state.includes('cancelled') &&
                            !p.state.includes('failed') &&
                            !p.state.includes('skipped')
                    )
                    .sort((a, b) => b.priority - a.priority);

                if (currentLocationPackages.length > 0)
                    currentLocationPackages = pkgResult.results
                        .filter((p) => p.spot_id === currentSpotId && !p.state.includes('cancelled') && !p.state.includes('failed'))
                        .sort((a, b) => b.priority - a.priority);

                if (currentLocationPackages.length > 0) {
                    const bestMatch = currentLocationPackages[0];
                    await localUpdatePackage(bestMatch);
                    await triggerPackage(bestMatch, navigationState.iot!);
                    onSubmit();
                    return;
                }

                let currentActive = pkgResult.results.find(
                    (p) =>
                        !p.state.includes('done') &&
                        !p.state.includes('cancelled') &&
                        !p.state.includes('failed') &&
                        !p.state.includes('skipped') &&
                        !p.state.includes('created')
                );

                if (!currentActive)
                    currentActive = pkgResult.results
                        .filter(
                            (p) => !p.state.includes('done') && !p.state.includes('cancelled') && !p.state.includes('failed') && !p.state.includes('skipped')
                        )
                        .sort((a, b) => b.priority - a.priority)[0];
                if (!currentActive) throw new Error('No active package found for transaction');

                await localUpdatePackage(currentActive);
                await refreshPackage();

                if (currentActive.spot_id === currentSpotId) {
                    await triggerPackage(currentActive, navigationState.iot!);
                    onSubmit();
                    return;
                }
            } catch (error) {
                Logger.error('Failed to resolve current location:', error);
            } finally {
                setIsLoading(false);
                checking.current = false;
            }
        };

        resolve();
    }, [_package, transaction, selectedOption]);

    useEffect(() => {
        if (transaction && transaction.id && store)
            SlotWorkflowClient.postAvailableSpots(transaction.account_id!, {
                account_id: transaction.account_id!,
                application: transaction.application ?? store.functionality.application,
                feature: transaction.feature ?? store.functionality.feature,
                transaction_type: transaction.type
            })
                .then((availableSpots) => {
                    setAvailableSpotIds(availableSpots.results.map((s) => s.id));
                })
                .catch((error) => {
                    Logger.error('Failed to fetch available spots:', error);
                });
    }, [transaction]);

    const onOptionA = async () => {
        if (!_package) return;
        setIsLoading(true);
        try {
            await triggerPackage(_package as Package, navigationState.iot!);
            const falseStep = store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT_FALSE);
            onSubmit(falseStep);
        } catch (error) {
            Logger.error('Failed to trigger package_continue:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onOptionB = async () => {
        if (!_package || !transaction || !currentSpotId || !dropWorkflowId || !pickWorkflowId) return;
        setIsLoading(true);
        try {
            const dropPkg = await PackageClient.postPackage(transaction.account_id!, transaction.id!, {
                priority: 4,
                workflow_id: dropWorkflowId as string,
                spot_id: currentSpotId,
                type: PackageType.DROP_OFF,
                blocking: true,
                application: transaction.application,
                feature: transaction.feature
            });

            const pickPkg = await PackageClient.postPackage(transaction.account_id!, transaction.id!, {
                priority: 3,
                workflow_id: pickWorkflowId as string,
                spot_id: currentSpotId,
                type: PackageType.PICK_UP,
                blocking: true,
                dependent_package_id: dropPkg.id,
                application: transaction.application,
                feature: transaction.feature
            });

            await PackageClient.patchPackage(_package.account_id!, _package.transaction_id!, _package.id!, { dependent_package_id: pickPkg.id });

            const allPackages = await PackageClient.fetchPackages({ transaction_id: transaction.id });
            updateWizardData({ packages: allPackages.results });
            await localUpdatePackage(dropPkg);

            const submitTrueStep = store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT_TRUE);
            await triggerPackage(dropPkg, navigationState.iot!);
            onSubmit(submitTrueStep);
        } catch (error) {
            Logger.error('Failed to create drop/pick packages:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const optionAEnabled = !!_package && !!_package.spot_id && availableSpotIds.includes(_package.spot_id) && _package.type === PackageType.DROP_OFF;
    const optionBEnabled =
        !!_package &&
        !!_package.spot_id &&
        !!transaction &&
        !!currentSpotId &&
        availableSpotIds.includes(currentSpotId) &&
        !!dropWorkflowId &&
        !!pickWorkflowId &&
        _package.type === PackageType.DROP_OFF;
    const noOptionsAvailable = !optionAEnabled && !optionBEnabled;

    useEffect(() => {
        if (selectedOption === null && _package) {
            if (optionBEnabled) setSelectedOption('B');
            else if (optionAEnabled) setSelectedOption('A');
        }
    }, [selectedOption, _package, optionAEnabled, optionBEnabled]);

    return {
        isLoading,
        currentSpot,
        destinationSpot,
        optionAEnabled,
        optionBEnabled,
        noOptionsAvailable,
        selectedOption,
        setSelectedOption,
        onSubmit: () => {
            if (selectedOption === 'A') {
                onOptionA();
            } else if (selectedOption === 'B') {
                onOptionB();
            }
        }
    };
};

export default useConfirmCurrentDeviceLocation;

async function triggerPackage(pkg: Package, navigationState: any) {
    await PackageWorkflowClient.triggerPackageState(pkg.account_id, pkg.transaction_id, pkg.id, {
        trigger: PackageTriggerName.PACKAGE_CONTINUE,
        location: navigationState.spot?.id ?? undefined,
        actor: navigationState.user?.id ?? undefined
    });
}
